import React from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import { canAccessRecordByLevel, isPermissionAllowed } from '../utils/permissions';
import { LEGACY_PERMISSION_MAP } from '../utils/permissionMaps';
import { Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

/**
 * کامپوننت PermissionGate برای محدود کردن دسترسی به بخش‌های UI
 * 
 * @param {string} permission - نام پرمیشن مورد نیاز (مثل 'add_lead', 'view_employees')
 * @param {string[]} permissions - آرایه‌ای از پرمیشن‌ها (اگر هر کدام داشت، نمایش می‌دهد)
 * @param {boolean} requireAll - اگر true باشد، همه پرمیشن‌های آرایه لازم است
 * @param {string} module - نام ماژول که باید فعال باشد
 * @param {React.ReactNode} children - محتوای نمایش داده شده اگر دسترسی داشت
 * @param {React.ReactNode} fallback - محتوای جایگزین اگر دسترسی نداشت (اختیاری)
 * @param {boolean} showAccessDenied - نمایش پیام عدم دسترسی (پیش‌فرض: false)
 * @param {function} onAccessDenied - callback در صورت عدم دسترسی
 * @param {number|null} recordOwnerId - شناسه مالک رکورد (برای بررسی سطح دسترسی)
 * @param {number|null} recordAddedById - شناسه ایجاد کننده رکورد
 * @param {boolean} debug - چاپ لاگ دیباگ در کنسول مرورگر
 */
const PermissionGate = ({
  permission,
  permissions = [],
  requireAll = false,
  module,
  resource,
  action,
  children,
  fallback = null,
  showAccessDenied = false,
  onAccessDenied,
  recordOwnerId = null,
  recordAddedById = null,
  debug = false,
}) => {
  const {
    hasPermission,
    isModuleActive,
    canAccessRecord,
    getActionLevel,
    isAdmin,
    isSuperAdmin,
  } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();

  const logDebug = (decision, reason, extra = {}) => {
    if (!debug) return;
    console.log('[PermissionGate DEBUG]', {
      decision,
      reason,
      permission,
      permissions,
      resource,
      action,
      module,
      user: {
        id: user?.id,
        roles: user?.roles?.map((r) => r.name || r),
        isAdmin,
        isSuperAdmin,
      },
      ...extra,
    });
  };

  if (isSuperAdmin || isAdmin) {
    logDebug('ALLOWED', isSuperAdmin ? 'Bypassed because user is SuperAdmin' : 'Bypassed because user is Admin');
    return <>{children}</>;
  }

  // بررسی ماژول فعال
  if (module && !isModuleActive(module)) {
    logDebug('DENIED', `Module "${module}" is inactive`);
    if (onAccessDenied) onAccessDenied();
    return showAccessDenied ? (
      <AccessDeniedMessage navigate={navigate} message="این ماژول برای شما فعال نیست" />
    ) : fallback;
  }

  if (resource && action) {
    const level = getActionLevel(resource, action);
    if (level != null && !isPermissionAllowed(level)) {
      logDebug('DENIED', `Permission level for resource "${resource}", action "${action}" is "${level}" (disallowed)`, { level });
      if (onAccessDenied) onAccessDenied();
      return showAccessDenied ? <AccessDeniedMessage navigate={navigate} /> : fallback;
    }
    if (
      level != null &&
      (recordOwnerId !== null || recordAddedById !== null) &&
      !canAccessRecordByLevel(level, {
        userId: user?.id,
        ownerId: recordOwnerId,
        addedById: recordAddedById,
      })
    ) {
      logDebug('DENIED', `Record ownership check failed for level "${level}"`, { level, recordOwnerId, recordAddedById });
      if (onAccessDenied) onAccessDenied();
      return showAccessDenied ? <AccessDeniedMessage navigate={navigate} /> : fallback;
    }
  }

  // اگر فقط یک پرمیشن داده شده (سازگاری با نام‌های قدیمی)
  if (permission) {
    const mapped = LEGACY_PERMISSION_MAP[permission];
    const mappedLevel = mapped ? getActionLevel(mapped.resource, mapped.action) : null;

    // بررسی سطح دسترسی به رکورد خاص
    if (recordOwnerId !== null || recordAddedById !== null) {
      const allowed = canAccessRecord(permission, recordOwnerId, recordAddedById);
      if (!allowed) {
        logDebug('DENIED', `canAccessRecord failed for permission "${permission}"`, { mapped, mappedLevel, recordOwnerId, recordAddedById });
        if (onAccessDenied) onAccessDenied();
        return showAccessDenied ? (
          <AccessDeniedMessage navigate={navigate} />
        ) : fallback;
      }
      logDebug('ALLOWED', `canAccessRecord granted for permission "${permission}"`, { mapped, mappedLevel, recordOwnerId, recordAddedById });
    } else if (!hasPermission(permission)) {
      logDebug('DENIED', `hasPermission("${permission}") returned false`, {
        mapped,
        mappedLevel,
        details:
          mappedLevel == null
            ? 'Permissions for this resource have not been loaded from API yet'
            : `Permission level "${mappedLevel}" does not allow access`,
      });
      if (onAccessDenied) onAccessDenied();
      return showAccessDenied ? (
        <AccessDeniedMessage navigate={navigate} />
      ) : fallback;
    } else {
      logDebug('ALLOWED', `hasPermission("${permission}") returned true`, { mapped, mappedLevel });
    }
  }

  // اگر آرایه‌ای از پرمیشن‌ها داده شده
  if (permissions.length > 0) {
    const hasAccess = requireAll
      ? permissions.every((p) => hasPermission(p))
      : permissions.some((p) => hasPermission(p));

    if (!hasAccess) {
      logDebug('DENIED', `Permissions array check failed (requireAll=${requireAll})`, { permissions });
      if (onAccessDenied) onAccessDenied();
      return showAccessDenied ? (
        <AccessDeniedMessage navigate={navigate} />
      ) : fallback;
    }
    logDebug('ALLOWED', `Permissions array check passed (requireAll=${requireAll})`, { permissions });
  }

  return <>{children}</>;
};

/**
 * کامپوننت نمایش پیام عدم دسترسی
 */
const AccessDeniedMessage = ({ navigate, message }) => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Result
      icon={<LockOutlined className="text-red-500" style={{ fontSize: '64px' }} />}
      title={
        <span style={{ fontFamily: 'CustomFont' }}>
          دسترسی محدود شده
        </span>
      }
      subTitle={
        <span style={{ fontFamily: 'CustomFont' }}>
          {message || 'شما مجوز دسترسی به این بخش را ندارید'}
        </span>
      }
      extra={
        <Button 
          type="primary" 
          onClick={() => navigate('/dashboard')}
          style={{ fontFamily: 'CustomFont' }}
        >
          بازگشت به داشبورد
        </Button>
      }
    />
  </div>
);

/**
 * HOC برای محدود کردن دسترسی به کامپوننت‌ها
 */
export const withPermission = (WrappedComponent, permissionConfig) => {
  return function PermissionWrapper(props) {
    return (
      <PermissionGate {...permissionConfig}>
        <WrappedComponent {...props} />
      </PermissionGate>
    );
  };
};

/**
 * کامپوننت برای نمایش دکمه فقط اگر دسترسی داشت
 */
export const PermissionButton = ({ 
  permission, 
  children, 
  ...buttonProps 
}) => (
  <PermissionGate permission={permission}>
    {React.cloneElement(children, buttonProps)}
  </PermissionGate>
);

/**
 * کامپوننت برای نمایش لینک فقط اگر دسترسی داشت
 */
export const PermissionLink = ({ 
  permission, 
  children,
  to,
  ...linkProps 
}) => (
  <PermissionGate permission={permission}>
    {children}
  </PermissionGate>
);

export default PermissionGate;
